import { useFormContext } from "react-hook-form";
import LinksIcon from "assets/Icons/Links.svg";

import { UserProfileSettingForm } from "pages/UserSettings";

import Icon, { BrandProfileImagePlaceHolder } from "components/atoms/Icon";
import ErrorMessage from "components/atoms/forms/ErrorMessage";
import { ProfileUploadWithCustomOnChange } from "components/atoms/ProfileUpload";
import ProfileImageElement from "components/molecules/ProfileImageElement";
import SocialLinkElement from "components/molecules/forms/SocialLinkElement";
import InputFieldWithLabel from "components/molecules/forms/InputFieldWithLabel";
import { useContext, useMemo } from "react";
import { UserContext } from "components/context/UserContext";
import { CheckCircleIcon } from "@heroicons/react/24/outline";
import AuthorizeButton from "./AuthorizeButton";

function UserPersonalInfoForm({ isOnboard = true }: { isOnboard?: boolean }) {
  const {
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<UserProfileSettingForm>();
  const { userDetails } = useContext(UserContext);

  const file = watch("profile_image");
  const existingImage = getValues("profile_image_url");

  const handleSetProfileImageForUser = (selectedFileList: FileList) => {
    setValue("profile_image", selectedFileList, {
      shouldValidate: true,
    });
  };

  const profileImageSrc = useMemo(() => (file ? URL.createObjectURL(file) : existingImage), [file]);

  return (
    <div className="mt-4 grid grid-cols-10 gap-x-4  gap-y-2 md:gap-x-12 ">
      {/* <div className="col-span-10 mb-4">
        <div className="-mb-8 space-x-3">
          <div>
            <ProfileImageElement
              label="Profile Image"
              profileImage={profileImageSrc}
              defaultImage={<BrandProfileImagePlaceHolder />}         code for using pencil icon
              alt="User Profile Image"
            />
          </div>
          <ProfileUploadWithCustomOnChange
            setFieldValue={handleSetProfileImageForUser}
            id="profile_image"
            showCropper
          />
        </div>
        <ErrorMessage errorMessage={errors.profile_image} />
      </div> */}

      <div className="col-span-10 ">
        <div className="flex flex-row space-x-3 ">
          <div>
            <ProfileImageElement
              label="Profile Image"
              profileImage={profileImageSrc}
              defaultImage={<BrandProfileImagePlaceHolder />}
              alt="User Profile Image"
            />
          </div>
          <div className="flex w-3/4 items-center">
            <ProfileUploadWithCustomOnChange
              labelClasses="mt-7"
              setFieldValue={handleSetProfileImageForUser}
              id="profile_image"
              showCropper
              label={file || existingImage ? "CHANGE IMAGE" : "UPLOAD IMAGE"}
            />
          </div>
        </div>
        <ErrorMessage errorMessage={errors.profile_image} />
      </div>

      <div className="col-span-10  md:col-span-5">
        <InputFieldWithLabel
          labelClasses="text-base sm:text-base xxl:text-xxs "
          additionalClasses="text-xxs sm:text-xxs xxl:text-xs"
          register={register}
          label="First Name"
          id="first_name"
          placeHolder="Please Enter First Name"
          errorMessage={errors.first_name?.message}
        />
      </div>
      <div className=" col-span-10 md:col-span-5">
        <InputFieldWithLabel
          labelClasses="text-base sm:text-base xxl:text-xxs "
          additionalClasses="text-xxs sm:text-xxs xxl:text-xs"
          register={register}
          label="Last Name"
          id="last_name"
          placeHolder="Please Enter Last Name"
          errorMessage={errors.last_name?.message}
        />
      </div>
      <div className="col-span-10 md:col-span-5">
        <InputFieldWithLabel
          labelClasses="text-base sm:text-base xxl:text-xxs "
          additionalClasses="text-xxs sm:text-xxs xxl:text-xs"
          register={register}
          label="Username*"
          id="username"
          placeHolder="Username"
          errorMessage={errors.username?.message}
        />
      </div>
      <div className="col-span-10 md:col-span-5">
        <InputFieldWithLabel
          labelClasses="text-base sm:text-base xxl:text-xxs "
          additionalClasses="text-xxs sm:text-xxs xxl:text-xs"
          register={register}
          label={isOnboard ? "Email" : "Email*"}
          id="email"
          placeHolder="Email"
          errorMessage={errors.email?.message}
          isDisabled={isOnboard}
        />
      </div>
      <div className="col-span-10 mb-2">
        <Icon labelClasses="sm:text-xs" src={LinksIcon} text="Links" imageContainerClasses="w-6 h-6" />
      </div>
      <div className="col-span-10 md:col-span-5">
        <SocialLinkElement
          fieldAddOnClasses="xl:text-xxs 2xl:text-sm  p-2 2xl:p-2  "
          labelClasses="text-xxs"
          additionalClasses="xl:text-xxs 2xl:text-xs p-2 2xl:p-2 placeholder:text-xs"
          getValues={getValues}
          setValue={setValue}
          id="twitter_link"
          label={
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1">
                Twitter
                {userDetails?.has_authorized_twitter && (
                  <CheckCircleIcon title="Linked Successfully" className="h-5 w-5 cursor-pointer text-lime-500" />
                )}
              </span>
              {!userDetails?.has_authorized_twitter && <AuthorizeButton authorizationFor="twitter" />}
            </div>
          }
          placeHolder="Enter Twitter Link"
          register={register}
          errorMessage={errors.twitter_link?.message}
        />
      </div>
      <div className=" col-span-10 md:col-span-5">
        <SocialLinkElement
          fieldAddOnClasses="xl:text-xxs 2xl:text-sm p-2 2xl:p-2"
          labelClasses="text-xxs"
          additionalClasses="xl:text-xs 2xl:text-xs p-2 2xl:p-2"
          getValues={getValues}
          setValue={setValue}
          id="instagram_link"
          label={
            <div className="flex items-center justify-between">
              <span className="flex items-center gap-1">
                Instagram
                <CheckCircleIcon
                  title="Linked Successfully"
                  className="invisible h-5 w-5 cursor-pointer text-lime-500"
                />
              </span>
              <AuthorizeButton authorizationFor="instagram" hidden />
            </div>
          }
          placeHolder="Enter Instagram Link"
          register={register}
          errorMessage={errors.instagram_link?.message}
        />
      </div>
      <div className="col-span-10 md:col-span-5">
        <SocialLinkElement
          fieldAddOnClasses="xl:text-xxs 2xl:text-sm p-2 2xl:p-2"
          labelClasses="text-xxs"
          additionalClasses="xl:text-xxs 2xl:text-xs p-2 2xl:p-2 "
          getValues={getValues}
          setValue={setValue}
          id="other_link"
          label="Other"
          placeHolder="Add other Links"
          register={register}
          errorMessage={errors.other_link?.message}
        />
      </div>
    </div>
  );
}
export default UserPersonalInfoForm;

UserPersonalInfoForm.defaultProps = {
  isOnboard: true,
};
